import type * as React from "react";

import { cx } from "../../lib/cx";
import { Icon, type IconName } from "./Icon";
import s from "./Badge.module.css";

export type BadgeTone = "neutral" | "outline" | "brand" | "accent" | "highlight" | "success" | "warning" | "danger" | "inverse" | "overlay";
export type BadgeSize = "sm" | "md";

export interface BadgeProps extends Omit<React.ComponentPropsWithoutRef<"button">, "type"> {
  /** neutral = surface con borde; outline = blanca con borde gray-300 (un borrador); brand = accent-100/accent; accent = cyan-100/cyan-700; highlight = cian sólido con
   *  tinta ("Nuevo"); success/warning/danger = estado; inverse = tinta con blanco (el precio); overlay = vidrio oscuro
   *  sobre una foto. */
  tone?: BadgeTone;
  size?: BadgeSize;
  icon?: IconName;
  /** Un punto de estado en lugar del icono. */
  dot?: boolean;
  /** `pill`: píldora, para un marcador chiquito pegado a un nombre ("vos"). Los estados y los datos siguen en radio 6. */
  shape?: "badge" | "pill";
  /** Un icono chico después del texto: la flecha de un badge que abre un menú. */
  iconAfter?: IconName;
  /** `button`: el badge es clickeable (el estado de una fila, que abre el menú para cambiarlo). Lleva `type="button"`,
   *  cursor de mano, hover y anillo de foco; la semántica de menú la pone `Menu`. */
  as?: "span" | "button";
}

/** Etiqueta chica de estado o de dato, radio 6. Sentence case, sin emoji. */
export function Badge({
  children,
  tone = "neutral",
  size = "md",
  icon,
  dot = false,
  shape = "badge",
  iconAfter,
  as: Tag = "span",
  className,
  ...rest
}: BadgeProps) {
  return (
    <Tag
      type={Tag === "button" ? "button" : undefined}
      className={cx(s.badge, className)}
      data-tone={tone}
      data-size={size}
      data-dot={dot || undefined}
      data-shape={shape === "pill" ? "pill" : undefined}
      data-interactive={Tag === "button" ? "" : undefined}
      {...rest}
    >
      {dot ? <span className={s.punto} /> : icon ? <Icon name={icon} size={size === "sm" ? 11 : 12} /> : null}
      {children}
      {iconAfter ? <Icon className={s.flecha} name={iconAfter} size={size === "sm" ? 10 : 11} /> : null}
    </Tag>
  );
}
